import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Camera, Upload, X, Image as ImageIcon, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function IdDocumentUpload({ 
  value, 
  onChange, 
  label = "ID Document Photo",
  guestId = null,
  disabled = false 
}) {
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Allowed: JPEG, PNG, WebP, PDF');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum 10MB allowed.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (guestId) {
        formData.append('guest_id', guestId);
      }

      const res = await api.post('/upload/id-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onChange(res.data.document_url);
      toast.success('ID document uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload ID document');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleCameraCapture = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleFileUpload(file);
    }
  };

  const removeDocument = () => {
    onChange(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  return (
    <div className="space-y-2">
      <Label className="text-gray-700 flex items-center gap-1">
        <ImageIcon className="h-3 w-3" />
        {label}
      </Label>

      {value ? (
        // Document uploaded - show preview
        <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
          <div 
            className="h-16 w-16 rounded-lg bg-white border border-green-200 overflow-hidden cursor-pointer flex items-center justify-center"
            onClick={() => setPreviewOpen(true)}
          >
            {value.endsWith('.pdf') ? (
              <span className="text-xs text-gray-500 font-medium">PDF</span>
            ) : (
              <img 
                src={getFullUrl(value)} 
                alt="ID Document" 
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span class="text-xs text-gray-500">Preview</span>';
                }}
              />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-green-700">Document Uploaded</p>
            <p className="text-xs text-green-600">Click to preview</p>
          </div>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setPreviewOpen(true)}
              className="h-8 w-8 text-green-600 hover:text-green-800"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={removeDocument}
              className="h-8 w-8 text-red-500 hover:text-red-700"
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        // No document - show upload buttons
        <div className="flex gap-2">
          {/* Camera capture - visible on mobile */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading || disabled}
            className="flex-1 border-blue-200 text-blue-600 hover:bg-blue-50"
            data-testid="camera-capture-btn"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Camera className="h-4 w-4 mr-1" />
            )}
            Camera
          </Button>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleCameraCapture}
            className="hidden"
          />

          {/* File upload */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || disabled}
            className="flex-1 border-purple-200 text-purple-600 hover:bg-purple-50"
            data-testid="file-upload-btn"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-1" />
            )}
            Upload
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>ID Document Preview</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center min-h-[300px] bg-gray-100 rounded-lg">
            {value?.endsWith('.pdf') ? (
              <div className="text-center">
                <p className="text-gray-500 mb-2">PDF Document</p>
                <Button
                  variant="outline"
                  onClick={() => window.open(getFullUrl(value), '_blank')}
                >
                  Open PDF
                </Button>
              </div>
            ) : (
              <img 
                src={getFullUrl(value)} 
                alt="ID Document" 
                className="max-h-[500px] max-w-full object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
